import { useEffect, useState } from 'react';
import { useSnapshot } from 'valtio';

import Link from '@cartwhl/common/components/Link';
import { completeHostedCheckout } from '@cartwhl/common/stores/checkoutStore';
import { getProductByID } from '@cartwhl/common/stores/productStore';
import { getStripePortalSession } from '@cartwhl/common/utils/getStripePortalSession';
import LogoMark from '@cartwhl/ui/atoms/LogoMark';
import Text from '@cartwhl/ui/base/Text';
import ChevronCompactLeftIcon from '@cartwhl/ui/icons/ChevronCompactLeftIcon';

import {
  authStore,
  checkoutStore,
  productStore,
  userStore,
} from 'stores/global';

export default function CheckoutPage() {
  const params = new URLSearchParams(window.location.search);
  const quantity = parseInt(params.get('quantity') || '1', 10);

  const { price } = useSnapshot(checkoutStore.state);
  const { authStatus } = useSnapshot(authStore.state);
  const { userInfo } = useSnapshot(userStore.state);

  const [requesting, setRequesting] = useState<boolean>(false);
  const [redirecting, setRedirecting] = useState<boolean>(false);

  useEffect(() => {
    checkoutStore.fillPrice();
  }, []);

  useEffect(() => {
    async function redirectToCheckout() {
      if (price && !redirecting) {
        setRedirecting(true);
        try {
          const stripeHostedCheckoutURL = await completeHostedCheckout(
            price,
            quantity,
          );
          if (stripeHostedCheckoutURL) {
            window.location.href = stripeHostedCheckoutURL;
          }
        } catch (error) {
          console.error('Failed to redirect to checkout:', error);
          setRedirecting(false);
        }
      }
    }
    redirectToCheckout();
  }, [price, quantity, redirecting]);

  async function requestPortalSession() {
    setRequesting(true);
    getStripePortalSession(userInfo.stripeCustomerID)
      .then(({ data }) => {
        if (typeof data !== 'undefined') {
          window.location.href = data.url;
        }
        setRequesting(false);
      })
      .catch(() => setRequesting(false));
  }

  if (price === null) {
    return (
      <main className="container flex min-h-full flex-col py-8 lg:flex-row lg:divide-x lg:py-16">
        <div className="flex flex-col lg:w-1/2 lg:flex-none lg:justify-between">
          <div className="flex flex-col gap-3 pb-4 lg:gap-4">
            <div className="lg:mb-4">
              <LogoMark />
            </div>
            <Text
              variant="web/body/medium/md"
              className="text-gray-60 animate-pulse"
            >
              Fetching Cartwheel purchase information...
            </Text>
          </div>
        </div>
      </main>
    );
  }

  const product = getProductByID(price.product);
  const isSpins = product?.name.includes('Spins');

  return (
    <main className="container flex min-h-full flex-col py-8 lg:flex-row lg:divide-x lg:py-16">
      <div className="flex flex-col lg:w-1/2 lg:flex-none lg:justify-between">
        <div className="flex flex-col gap-3 pb-4 lg:gap-4">
          <div className="lg:mb-4">
            <LogoMark />
          </div>
          <Text variant="web/body/medium/md" className="text-gray-60">
            {price.recurring
              ? 'Become a Cartwheel Professional'
              : isSpins
                ? 'Buy more Spins'
                : 'Enhance your Cartwheel experience'}
          </Text>
          <div>
            <Text as="span" variant="web/display/lg">
              {checkoutStore.formatMoney(price, quantity)}
            </Text>
            {price.recurring && (
              <Text as="span" variant="web/body/md" className="ml-2">
                / {productStore.formatInterval(price.recurring?.interval)}
              </Text>
            )}
          </div>
          <Text as="h3" variant="web/body/md">
            {product?.name}
          </Text>
          <ul className="mx-5 mb-2 list-disc border-b pb-4 opacity-40">
            {product?.marketing_features.map(({ name }, i) => (
              <Text key={i} as="li" variant="web/body/md" className="my-1">
                {name}
              </Text>
            ))}
          </ul>
          <Link
            href={`/pricing?${params.toString()}`}
            className="text-gray-60 hover:text-gray-90 flex transition-colors"
          >
            <ChevronCompactLeftIcon className="h-4" />
            <Text variant="web/body/md">More information on purchase</Text>
          </Link>
          {authStatus === 'user' && (
            <button
              disabled={requesting}
              onClick={requestPortalSession}
              className="text-gray-60 hover:text-gray-90 flex transition-colors"
            >
              <ChevronCompactLeftIcon className="h-4" />
              <Text variant="web/body/md">Manage billing</Text>
            </button>
          )}
        </div>
        <Information className="hidden lg:flex" />
      </div>
      <div className="flex flex-col lg:w-1/2 lg:flex-none lg:justify-center">
        <div className="flex flex-col items-center justify-center p-8">
          <Text
            variant="web/body/medium/md"
            className="text-gray-60 animate-pulse"
          >
            Redirecting to secure checkout...
          </Text>
        </div>
      </div>
      <Information className="lg:hidden" />
    </main>
  );
}

function StripeLogo() {
  return (
    <svg
      viewBox="0 0 60 25"
      xmlns="http://www.w3.org/2000/svg"
      width="50"
      fill="currentColor"
    >
      <title>Stripe</title>
      <path
        d="M59.64 14.28h-8.06c.19 1.93 1.6 2.55 3.2 2.55 1.64 0 2.96-.37 4.05-.95v3.32a8.33 8.33 0 0 1-4.56 1.1c-4.01 0-6.83-2.5-6.83-7.48 0-4.19 2.39-7.52 6.3-7.52 3.92 0 5.96 3.28 5.96 7.5 0 .4-.04 1.26-.06 1.48zm-5.92-5.62c-1.03 0-2.17.73-2.17 2.58h4.25c0-1.85-1.07-2.58-2.08-2.58zM40.95 20.3c-1.44 0-2.32-.6-2.9-1.04l-.02 4.63-4.12.87V5.57h3.76l.08 1.02a4.7 4.7 0 0 1 3.23-1.29c2.9 0 5.62 2.6 5.62 7.4 0 5.23-2.7 7.6-5.65 7.6zM40 8.95c-.95 0-1.54.34-1.97.81l.02 6.12c.4.44.98.78 1.95.78 1.52 0 2.54-1.65 2.54-3.87 0-2.15-1.04-3.84-2.54-3.84zM28.24 5.57h4.13v14.44h-4.13V5.57zm0-4.7L32.37 0v3.36l-4.13.88V.88zm-4.32 9.35v9.79H19.8V5.57h3.7l.12 1.22c1-1.77 3.07-1.41 3.62-1.22v3.79c-.52-.17-2.29-.43-3.32.86zm-8.55 4.72c0 2.43 2.6 1.68 3.12 1.46v3.36c-.55.3-1.54.54-2.89.54a4.15 4.15 0 0 1-4.27-4.24l.01-13.17 4.02-.86v3.54h3.14V9.1h-3.13v5.85zm-4.91.7c0 2.97-2.31 4.66-5.73 4.66a11.2 11.2 0 0 1-4.46-.93v-3.93c1.38.75 3.1 1.31 4.46 1.31.92 0 1.53-.24 1.53-1C6.26 13.77 0 14.51 0 9.95 0 7.04 2.28 5.3 5.62 5.3c1.36 0 2.72.2 4.09.75v3.88a9.23 9.23 0 0 0-4.1-1.06c-.86 0-1.44.25-1.44.9 0 1.85 6.29.97 6.29 5.88z"
        fillRule="evenodd"
      />
    </svg>
  );
}

function Information({ className }: { className?: string }) {
  return (
    <div
      className={`text-gray-45 flex flex-col-reverse gap-3 sm:flex-row ${className}`}
    >
      <div className="flex gap-3">
        <Link href="/terms-of-use">Terms</Link>
        <Link href="/privacy-policy">Privacy policy</Link>
      </div>
      <span className="hidden sm:inline">|</span>
      <div className="flex gap-1.5">
        Powered by <StripeLogo />
      </div>
    </div>
  );
}
